/**
 * Baby Bento 2026 Guide Engine - Master Build
 * Universal Renderer with Attribute Priority & Cache Busting
 */

function renderSpecs(brand) {
  let specsHtml = '<div class="lg-specs-grid">';
  
  // Handle potential custom 'specs' field safely
  const specs = brand.specs || "";

  if (specs) {
    specsHtml += `<div class="lg-specs-label">Specs:</div><div class="lg-specs-value">${specs}</div>`;
  }
  if (brand.colours) {
    specsHtml += `<div class="lg-specs-label">Colours:</div><div class="lg-specs-value">${brand.colours}</div>`;
  }
  if (brand.capacity || brand.sizes) {
    specsHtml += `<div class="lg-specs-label">Capacity:</div><div class="lg-specs-value">${brand.capacity || brand.sizes}</div>`;
  }
  if (brand.price_range) {
    specsHtml += `<div class="lg-specs-label">Price Range:</div><div class="lg-specs-value">${brand.price_range}</div>`;
  }
  if (brand.dimensions) {
    specsHtml += `<div class="lg-specs-label">Dimensions (W x H x D):</div><div class="lg-specs-value">${brand.dimensions}</div>`;
  }

  // Safely handle compatibility list (supports both Array and comma-separated String)
  let compatibility = "";
  if (Array.isArray(brand.compatible_lunchboxes)) {
    compatibility = brand.compatible_lunchboxes.join(", ");
  } else if (typeof brand.compatible_lunchboxes === 'string') {
    compatibility = brand.compatible_lunchboxes;
  }

  if (compatibility) {
    specsHtml += `<div class="lg-specs-compatibility-label">Fits popular lunchboxes:</div><div class="lg-specs-compatibility-value">${compatibility}</div>`;
  }
  specsHtml += "</div>";
  return specsHtml;
}

function renderProductCarousel(section) {
  if (!section || section.shopify_module !== "crunch-sip-carousel") return "";

  const products = Array.isArray(section.products) ? section.products : [];
  if (!products.length) return "";

  let html = '<div class="lg-product-carousel">';

  products.forEach(function (product) {
    const image = product.image && typeof product.image === 'object' ? product.image : { src: product.image || '', alt: product.alt || product.name || 'Product image' };
    const imageSrc = image.src || '';
    const imageAlt = image.alt || product.name || 'Product image';
    const title = product.title || product.name || 'Product';
    const price = product.price || '';
    const features = Array.isArray(product.features) ? product.features : [];
    const cta = product.cta || {};
    const ctaLink = cta.link || product.link || '#';
    const ctaLabel = cta.label || 'Shop Now';

    html += `
      <article class="lg-product-card">
        ${imageSrc ? `<img src="${imageSrc}" alt="${imageAlt}" class="lg-product-image">` : ''}
        <div class="lg-product-body">
          <h3 class="lg-product-title">${title}</h3>
          ${price ? `<p class="lg-product-price">${price}</p>` : ''}
          ${features.length ? `<ul class="lg-product-bullets">${features.map(f => `<li>${f}</li>`).join('')}</ul>` : ''}
          <a href="${ctaLink}" class="lg-brand-cta">${ctaLabel}</a>
        </div>
      </article>`;
  });

  html += '</div>';
  return html;
}

function renderFaq(faqData) {
  if (!faqData || faqData.length === 0) return "";
  
  let faqHtml = `
    <section class="lg-faq-section">
      <h2 class="lg-heading">Frequently Asked Questions</h2>
      <div class="lg-faq-list">`;
  
  faqData.forEach(function (item) {
    faqHtml += `
      <details class="lg-faq-item">
        <summary class="lg-faq-question">${item.q}</summary>
        <div class="lg-faq-answer">${item.a}</div>
      </details>`;
  });
  
  faqHtml += `</div></section>`;
  return faqHtml;
}

function renderFooter(footerData) {
  // More robust check to ensure all required data points exist before rendering.
  if (!footerData || !footerData.heading || !footerData.body || !footerData.cta || !footerData.cta.link || !footerData.cta.label) {
    console.warn("Guide Engine: Footer data incomplete or missing. Rendering a default footer.");
    // Provide a default footer object to ensure a footer is always rendered
    footerData = {
      heading: "Ready to Upgrade Your Lunchbox Game?",
      body: "Explore our full collection of lunch solutions designed for Australian families.",
      cta: { label: "Shop All Lunchboxes", link: "/collections/all" },
    };
  }
  return `
    <footer class="lg-footer">
      <div class="lg-footer-content-wrapper">
        <div class="lg-footer-column">
          <h2 class="lg-footer-heading">${footerData.heading}</h2>
          <div class="lg-footer-body">${footerData.body}</div>
          <div class="lg-cta-wrapper">
            <a href="${footerData.cta.link}" class="lg-brand-cta">${footerData.cta.label}</a>
          </div>
        </div>
      </div>
    </footer>
  `;
}

function injectModularSchema(data) {
  if (!data) return;

  const guideSchema = {
    "@context": "https://schema.org",
    "@type": "Guide",
    "name": data.title || "Baby Bento Guide",
    "abstract": (data.introduction || "").replace(/<[^>]*>?/gm, ''),
    "author": { "@type": "Organization", "name": "Baby Bento" }
  };

  if (data.schema_metadata) {
    if (data.schema_metadata.datePublished) {
      guideSchema.datePublished = data.schema_metadata.datePublished;
    }
    if (data.schema_metadata.dateModified) {
      guideSchema.dateModified = data.schema_metadata.dateModified;
    }
  }

  const guideScript = document.createElement('script');
  guideScript.type = 'application/ld+json';
  guideScript.text = JSON.stringify(guideSchema);
  document.head.appendChild(guideScript);

  if (Array.isArray(data.faq) && data.faq.length) {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": data.faq.map(function (item) {
        return {
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": (item.a || "").replace(/<[^>]*>?/gm, '')
          }
        };
      })
    };

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);
  }
}

async function renderGuide() {
  const container = document.getElementById("guide-mount") ||
                    document.getElementById("guide-content") || 
                    document.getElementById("lunch-guide-content") || 
                    document.getElementById("stainless-steel-content");
  
  if (!container) return;

  // 1. DYNAMIC DATA SELECTION: Priority to Attribute -> window variable -> old defaults
  const dataUrl = container.getAttribute('data-json-url') || 
                  window.guideDataUrl || 
                  window.lunchGuideDataUrl || 
                  window.steelGuideDataUrl;
  
  if (!dataUrl) {
    console.error("Guide Engine: No data URL provided.");
    return;
  }

  try {
    // 2. CACHE BUSTING: Forces Shopify to fetch the latest version
    const cacheBuster = dataUrl.includes('?') ? `&t=${Date.now()}` : `?t=${Date.now()}`;
    const response = await fetch(dataUrl + cacheBuster);
    
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const guideData = await response.json();
    
    // SEO & Rendering
    injectModularSchema(guideData); 

    let html = "";
    const iconHtml = '<img src="https://cdn.shopify.com/s/files/1/1591/7213/files/love-heart-star.png?v=1764041268" alt="Heart Star" class="lg-icon-star" style="width:30px; margin-left:10px; vertical-align:middle;">';

    html += `
      <header class="lg-hero">
          <h1 class="lg-title">${guideData.title}${iconHtml}</h1>
          <p class="lg-subtitle">${guideData.introduction}</p>
      </header>
    `;

    if (guideData.sections) {
      guideData.sections.forEach(function (section) {
        html += `
          <section class="lg-section">
            <h2 class="lg-heading">${section.heading}</h2>
            <div class="lg-section-content">${section.content}</div>`;

        // Render Benefits, Steps, Comparison Table, and Ideas
        if (section.benefits) {
          html += '<ul class="lg-benefits-list">';
          section.benefits.forEach(b => html += `<li><strong>${b.title}:</strong> ${b.description}</li>`);
          html += '</ul>';
        }
        if (section.steps) {
          html += '<ol class="lg-steps-list">';
          section.steps.forEach(s => html += `<li>${s}</li>`);
          html += '</ol>';
        }
        if (section.comparison_table) {
          html += '<div class="lg-table-wrapper"><table class="lg-comparison-table"><thead><tr>';
          section.comparison_table.headers.forEach(h => html += `<th>${h}</th>`);
          html += '</tr></thead><tbody>';
          section.comparison_table.rows.forEach(r => {
            html += '<tr>';
            Object.values(r).forEach(val => html += `<td>${val}</td>`);
            html += '</tr>';
          });
          html += '</tbody></table></div>';
        }
        if (section.ideas) {
          html += '<ul class="lg-ideas-list">';
          section.ideas.forEach(i => html += `<li>${i}</li>`);
          html += '</ul>';
        }

        const moduleHtml = renderProductCarousel(section);
        if (moduleHtml) {
          html += moduleHtml;
        }

        if (section.brands && section.brands.length > 0) {
          html += '<div class="lg-brand-grid">';
          section.brands.forEach(function (brand) {
            const imageObj = (brand.image && typeof brand.image === 'object') ? brand.image : null;
            const imageSrc = imageObj ? imageObj.src : (brand.image || 'https://placehold.co/400x400?text=No+Image+Provided');
            const imageAlt = imageObj ? (imageObj.alt || brand.name) : (brand.alt || brand.name);

            html += `
              <div class="lg-brand-card">
                ${brand.image ? `
                <div class="lg-brand-image-wrapper">
                  <img src="${imageSrc}" alt="${imageAlt}" class="lg-brand-image" onerror="this.onerror=null;this.src='https://placehold.co/400x400?text=Image+Unavailable';">
                </div>` : ''}
                <div class="lg-brand-content">
                  <h3 class="lg-brand-name">${brand.name}</h3>
                  <p class="lg-brand-desc">${brand.description}</p>`;
            
            html += renderSpecs(brand);

            if (brand.shopify_buy_button) {
              html += brand.shopify_buy_button;
            } else {
              const link = brand.product_link || (brand.cta ? brand.cta.link : "javascript:void(0)");
              const btnLabel = (brand.cta ? brand.cta.label : "Shop Now");
              html += `
                <div class="lg-cta-wrapper">
                  <a href="${link}" class="lg-brand-cta" ${link === 'javascript:void(0)' ? '' : 'target="_blank"'}>${btnLabel}</a>
                </div>`;
            }
            html += '</div></div>'; 
          });
          html += '</div>';
        }
        html += '</section>';
      });
    }

    // Render FAQ
    html += renderFaq(guideData.faq);
    
    // Render Footer (NEW!)
    html += renderFooter(guideData.footer);

    container.innerHTML = html;
    if (guideData.sections) {
      guideData.sections.forEach(function(section) {
        console.log("Module type:", section.shopify_module, JSON.stringify(section).substring(0, 100));
      });
    }
    document.dispatchEvent(new CustomEvent('guide:rendered'));

    // Manually execute scripts to activate Shopify Buy Buttons
    container.querySelectorAll("script").forEach(oldScript => {
      const newScript = document.createElement("script");
      
      // Safely copy attributes
      if (oldScript.attributes) {
        Array.from(oldScript.attributes).forEach(attr => {
          if (attr.name) {
            newScript.setAttribute(attr.name, attr.value || "");
          }
        });
      }

      newScript.appendChild(document.createTextNode(oldScript.innerHTML || ""));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

  } catch (error) {
    console.error("Error fetching guide data:", error);
    container.innerHTML = "<p style='text-align:center; padding: 20px;'>Error loading guide data. Please refresh the page.</p>";
  }
}

async function initGuideEngine() {
    await renderGuide(); 
}

document.addEventListener("DOMContentLoaded", initGuideEngine);
window.renderLunchGuide = renderGuide;